import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import FunnelView from '../components/FunnelView';
import { PerformanceAnalysisCard } from '../components/PerformanceAnalysisModal';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { AlertTriangle, TrendingUp, TrendingDown, Users, Copy, Check, Download, ChevronDown, ChevronUp } from 'lucide-react';


const getRangeDates = (preset) => {
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];
  const daysAgo = (n) => { const d = new Date(); d.setDate(today.getDate() - n); return d; };

  // ISO week: Monday is day 1, Sunday is day 7
  // JS getDay(): Sunday=0, Monday=1, ..., Saturday=6
  const dayOfWeek = today.getDay();
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  switch (preset) {
    case 'this_week': {
      const monday = new Date(today);
      monday.setDate(today.getDate() - daysSinceMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { from: fmt(monday), to: fmt(sunday) };
    }
    case 'last_week': {
      const lastMonday = new Date(today);
      lastMonday.setDate(today.getDate() - daysSinceMonday - 7);
      const lastSunday = new Date(today);
      lastSunday.setDate(today.getDate() - daysSinceMonday - 1);
      return { from: fmt(lastMonday), to: fmt(lastSunday) };
    }
    case 'this_month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: fmt(first), to: fmt(today) };
    }
    case 'last_30_days':
      return { from: fmt(daysAgo(29)), to: fmt(today) };
    default:
      return { from: fmt(daysAgo(6)), to: fmt(today) };
  }
};

const PRESET_LABELS = {
  this_week: 'This Week',
  last_week: 'Last Week',
  this_month: 'This Month',
  last_30_days: 'Last 30 Days',
};

const SECTION_HEADER_STYLE = {
  fontSize: 13,
  fontWeight: 600,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginTop: 24,
  marginBottom: 8,
};

const Dashboard = () => {
  const { isAdminOrTL, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [leadsData, setLeadsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('this_week');
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showMessageTypes, setShowMessageTypes] = useState(false);

  const handleExportCSV = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { from, to } = getRangeDates(preset);
      const res = await api.get(`/stats/export?from=${from}&to=${to}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `softradix_stats_${from}_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const { from, to } = getRangeDates(preset);
        const [dashRes, leadsRes] = await Promise.all([
          api.get(`/stats/dashboard?from=${from}&to=${to}`),
          api.get('/leads/dashboard-counts'),
        ]);
        setData(dashRes.data);
        setLeadsData(leadsRes.data);
      } catch (err) {
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [preset]);

  const handleCopyStats = () => {
    if (!data) return;
    const t = data.today || {};
    const weekTotals = (data.thisWeekByBDE || []).reduce((acc, b) => {
      acc.cr_sent += Number(b.cr_sent || 0);
      acc.accepted += Number(b.accepted || 0);
      acc.replies += Number(b.replies || 0);
      acc.handoffs += Number(b.handoffs || 0);
      return acc;
    }, { cr_sent: 0, accepted: 0, replies: 0, handoffs: 0 });

    const pct = (num, den) => den > 0 ? `${((num / den) * 100).toFixed(1)}%` : '—';

    const lines = [];
    lines.push(`SoftRadix LinkedIn Stats — ${PRESET_LABELS[preset]}`);
    lines.push('');
    lines.push(`CRs Sent: ${weekTotals.cr_sent}`);
    lines.push(`Accepted: ${weekTotals.accepted} (${pct(weekTotals.accepted, weekTotals.cr_sent)})`);
    lines.push(`Replies: ${weekTotals.replies} (${pct(weekTotals.replies, weekTotals.accepted)})`);
    lines.push(`Handoffs: ${weekTotals.handoffs}`);
    lines.push('');
    lines.push(`Today: ${t.cr_sent || 0} CRs / ${t.accepted || 0} acc / ${t.replies || 0} rep / ${t.handoffs || 0} handoffs`);

    if (leadsData) {
      lines.push('');
      lines.push(`Pipeline: ${leadsData.team_pipeline.hot} Hot / ${leadsData.team_pipeline.warm} Warm / ${leadsData.team_pipeline.cold} Cold`);
      lines.push(`Pending validation: ${leadsData.pending_validation} | Added today: ${leadsData.added_today}`);
    }

    if ((data.thisWeekByBDE || []).length > 0) {
      lines.push('');
      lines.push('Per BDE:');
      data.thisWeekByBDE.forEach(b => {
        lines.push(`${b.name}: ${b.cr_sent || 0} CRs / ${b.accepted || 0} acc / ${b.replies || 0} rep / ${b.handoffs || 0} handoffs`);
      });
    }

    if ((data.missingToday || []).length > 0) {
      lines.push('');
      lines.push(`Missing today's log: ${data.missingToday.map(u => u.name).join(', ')}`);
    }

    const text = lines.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <div className="empty-state"><p>Loading dashboard...</p></div>;
  if (!data) return <div className="empty-state"><p>Unable to load dashboard.</p></div>;

  const t = data.today || {};
  const rangeLabel = PRESET_LABELS[preset];
  const calcChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Sum selected-range totals from BDE breakdown to compute rates
  const weekTotals = (data.thisWeekByBDE || []).reduce((acc, b) => {
    acc.cr_sent += Number(b.cr_sent || 0);
    acc.accepted += Number(b.accepted || 0);
    acc.replies += Number(b.replies || 0);
    return acc;
  }, { cr_sent: 0, accepted: 0, replies: 0 });

  const acceptanceRate = weekTotals.cr_sent > 0
    ? ((weekTotals.accepted / weekTotals.cr_sent) * 100).toFixed(1)
    : null;
  const replyRate = weekTotals.accepted > 0
    ? ((weekTotals.replies / weekTotals.accepted) * 100).toFixed(1)
    : null;

  // Build per-BDE leads lookup for the breakdown table
  const bdeLeadsMap = {};
  (leadsData?.per_bde || []).forEach(b => { bdeLeadsMap[b.user_id] = b; });

  const StatCard = ({ label, value, prevValue }) => {
    const change = calcChange(value, prevValue);
    return (
      <div className="stat-card">
        <div className="stat-value">{value || 0}</div>
        <div className="stat-label">{label}</div>
        {change !== null && (
          <div className={`stat-change ${parseFloat(change) >= 0 ? 'positive' : 'negative'}`}>
            {parseFloat(change) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {' '}{change}% vs previous period
          </div>
        )}
      </div>
    );
  };

  const RateCard = ({ label, value, sublabel }) => (
    <div className="stat-card">
      <div className="stat-value">{value !== null ? `${value}%` : '—'}</div>
      <div className="stat-label">{label}</div>
      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>{sublabel}</div>
    </div>
  );

  const PipelineCard = ({ label, value, accentColor }) => (
    <div className="stat-card" style={{ borderLeft: `4px solid ${accentColor}` }}>
      <div className="stat-value">{value || 0}</div>
      <div className="stat-label">{label}</div>
    </div>
  );

  const chartData = (data.thisWeekByBDE || []).map(b => ({
    name: b.name?.split(' ')[0] || 'Unknown',
    'CRs Sent': b.cr_sent || 0,
    'Accepted': b.accepted || 0,
    'Replies': b.replies || 0,
    'Handoffs': b.handoffs || 0,
  }));

  const pipeline = leadsData?.team_pipeline || { hot: 0, warm: 0, cold: 0 };
  const pendingValidation = leadsData?.pending_validation ?? 0;
  const addedToday = leadsData?.added_today ?? 0;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2>Team Dashboard</h2>
          <p>Team activity and pipeline at a glance</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isAdminOrTL && (
            <button
              onClick={handleExportCSV}
              disabled={exporting}
              style={{
                padding: '6px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                fontSize: 14,
                background: '#FFFFFF',
                cursor: exporting ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: '#111827',
                opacity: exporting ? 0.6 : 1,
              }}
            >
              <Download size={14} />
              {exporting ? 'Exporting...' : 'Download CSV'}
            </button>
          )}
          <button
            onClick={handleCopyStats}
            disabled={copied}
            style={{
              padding: '6px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              fontSize: 14,
              background: copied ? '#DCFCE7' : '#FFFFFF',
              cursor: copied ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: copied ? '#166534' : '#111827',
            }}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy Stats'}
          </button>
          <div>
            <label style={{ fontSize: 12, color: '#6B7280', marginRight: 8 }}>Range:</label>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                fontSize: 14,
                background: '#FFFFFF',
                cursor: 'pointer',
              }}
            >
              {Object.entries(PRESET_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* SECTION 1 — SALES FUNNEL */}
      <FunnelView {...getRangeDates(preset)} />

      {/* SECTION 2 — LEAD PIPELINE */}
      <div style={SECTION_HEADER_STYLE}>Lead Pipeline</div>
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <PipelineCard label="Hot" value={pipeline.hot} accentColor="#DC2626" />
        <PipelineCard label="Warm" value={pipeline.warm} accentColor="#F59E0B" />
        <PipelineCard label="Cold" value={pipeline.cold} accentColor="#2563EB" />
      </div>
      <div style={{ fontSize: 13, color: '#6B7280', marginTop: 6, marginBottom: 4 }}>
        {pendingValidation} pending validation · {addedToday} added today
      </div>

      {/* SECTION 3 — THIS PERIOD'S PERFORMANCE */}
      <div style={SECTION_HEADER_STYLE}>{rangeLabel} Performance</div>
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <RateCard
          label="Acceptance Rate"
          value={acceptanceRate}
          sublabel={`${weekTotals.accepted} accepted / ${weekTotals.cr_sent} CRs sent`}
        />
        <RateCard
          label="Reply Rate"
          value={replyRate}
          sublabel={`${weekTotals.replies} replies / ${weekTotals.accepted} accepted`}
        />
      </div>

      {/* SECTION 4 — TODAY'S MESSAGE MIX */}
      <div style={SECTION_HEADER_STYLE}>Today's Message Mix</div>

      <button
        onClick={() => setShowMessageTypes(v => !v)}
        style={{
          marginTop: 12,
          padding: '6px 12px',
          border: '1px solid #D1D5DB',
          borderRadius: 6,
          fontSize: 13,
          background: '#FFFFFF',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          color: '#374151',
        }}
      >
        {showMessageTypes ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {showMessageTypes ? 'Hide message breakdown' : 'Show message breakdown'}
      </button>

      {showMessageTypes && (
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginTop: 8 }}>
          <div className="stat-card">
            <div className="stat-value" style={{ fontSize: 24 }}>{t.generic || 0}</div>
            <div className="stat-label">Generic Messages</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ fontSize: 24 }}>{t.personalized || 0}</div>
            <div className="stat-label">Personalized Messages</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ fontSize: 24 }}>{t.type_a || 0}</div>
            <div className="stat-label">Type A</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ fontSize: 24 }}>{t.type_b_reveal || 0}</div>
            <div className="stat-label">B-Reveal</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ fontSize: 24 }}>{t.type_b_confirm || 0}</div>
            <div className="stat-label">B-Confirm</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ fontSize: 24 }}>{t.type_b_no_reply || 0}</div>
            <div className="stat-label">B-No Reply</div>
          </div>
        </div>
      )}

      {/* SECTION 5 — TEAM */}
      <div style={SECTION_HEADER_STYLE}>Team</div>

      {data.missingToday?.length > 0 && (
        <div className="card" style={{ background: '#FFF3E0', borderLeft: '4px solid #E65100' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} color="#E65100" />
            <strong style={{ color: '#E65100' }}>Missing today's log:</strong>
            <span>{data.missingToday.map(u => u.name).join(', ')}</span>
          </div>
        </div>
      )}

      {chartData.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3>{rangeLabel} by BDE</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fontSize: 13 }} />
              <YAxis tick={{ fontSize: 13 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="CRs Sent" fill="#D1D5DB" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Accepted" fill="#2E75B6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Replies" fill="#1F3A5F" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Handoffs" fill="#2E7D32" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3>BDE Breakdown — {rangeLabel}</h3>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>BDE</th><th>CRs</th><th>Accepted</th><th>Replies</th>
                <th>Qualified</th><th>Handoffs</th><th>Calls</th>
                <th>Hot</th><th>Warm</th><th>Cold</th>
                <th>Generic</th><th>Personal</th><th>Days Logged</th>
              </tr>
            </thead>
            <tbody>
              {(data.thisWeekByBDE || []).map(b => {
                const lp = bdeLeadsMap[b.id] || { hot: 0, warm: 0, cold: 0 };
                return (
                  <tr key={b.id}>
                    <td><strong>{b.name}</strong></td>
                    <td>{b.cr_sent || 0}</td>
                    <td>{b.accepted || 0}</td>
                    <td>{b.replies || 0}</td>
                    <td>{b.qualified || 0}</td>
                    <td><strong>{b.handoffs || 0}</strong></td>
                    <td>{b.calls || 0}</td>
                    <td style={{ color: '#DC2626', fontWeight: 600 }}>{lp.hot}</td>
                    <td style={{ color: '#B45309', fontWeight: 600 }}>{lp.warm}</td>
                    <td style={{ color: '#1D4ED8', fontWeight: 600 }}>{lp.cold}</td>
                    <td>{b.generic || 0}</td>
                    <td>{b.personalized || 0}</td>
                    <td>{b.days_logged || 0}/{data.range?.days || 7}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* SECTION 6 — PERFORMANCE ANALYSIS (admin-only) */}
      {isAdmin && <PerformanceAnalysisCard />}
    </div>
  );
};

export default Dashboard;
